import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Title from '@cashew/title';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BackArrow, PageWithNavigation } from '../../components';
import MerchantSection from './MerchantSection';
import { type MerchantData, merchantEndpoint } from './util';
import BusinessSection from './BusinessSection';
import BankSection from './BankSection';
import Tabs from './Tabs';
import type { UserState } from '../../reducers/user';
import type { RootState } from '../../reducers';
import ConfigurationsSection from './ConfigurationsSection';

const { api } = require('@cashew/utils');

// export const merchantOnboardingPage = (language: string, merchantId: number) => `/${language}/merchants/${merchantId}`;

const ns = 'merchant-onboarding';

interface MerchantResponse {
	data: MerchantData;
	status: string;
}

/**
 * Refunds Page RefundPage
 */
export default () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { merchantId } = useParams<{ merchantId: string }>();
	const [merchantData, setMerchantData] = useState<MerchantData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const { language, accessType }: UserState = useSelector(
		(state: RootState) => state.user,
	);
	const [currentTab, setCurrentTab] = useState(
		accessType === 'WRITE' ? 'configurations' : 'merchantInformation',
	);

	const [, setIsMerchantSectionValid] = useState(false);
	const [, setIsBusinessSectionValid] = useState(false);
	const [, setIsBankSectionValid] = useState(false);

	useEffect(() => {
		api
			.get({
				endpoint: merchantEndpoint(merchantId),
			})
			.then((respMerchant: MerchantResponse) => {
				setMerchantData(respMerchant.data);
				setIsLoading(false);
			})
			.catch(() => {
				dispatch({
					type: 'OPEN_TOASTER',
					payload: {
						toasterTitle: t('merchantOnboarding.error.title.request'),
						toasterMessage: t('merchantOnboarding.error.message.request'),
						toasterType: 'error',
						toasterVisible: true,
						toasterDismiss: null,
					},
				});

				setIsLoading(false);
			});
	}, [merchantId]);

	return (
		<div className={ns}>
			<Helmet>
				<title>
					{t('merchantOnboarding.sectionTitle', {
						name: merchantData?.companyName ?? t('filed.empty'),
					})}
				</title>
			</Helmet>
			<PageWithNavigation>
				<Title>
					{merchantData?.companyName ?? t('filed.empty')}
					<BackArrow to={`/${language}/merchants`} title="Back to merchants" />
				</Title>
				<Tabs
					selected={
						accessType === 'WRITE' ? 'configurations' : 'merchantInformation'
					}
					tabs={[
						{
							key: 'configurations',
							value: 'Configurations',
							hidden: accessType !== 'WRITE',
						},
						{ key: 'merchantInformation', value: 'Merchant information' },
						{ key: 'businessInformation', value: 'Business information' },
						{ key: 'bankInformation', value: 'Bank information' },
					]}
					currentTab={setCurrentTab}
				/>

				{currentTab === 'configurations' && accessType === 'WRITE' && (
					<ConfigurationsSection
						merchantData={merchantData}
						isLoading={isLoading}
						setIsSectionValid={setIsMerchantSectionValid}
					/>
				)}
				{currentTab === 'merchantInformation' && (
					<MerchantSection
						merchantData={merchantData}
						isLoading={isLoading}
						setIsSectionValid={setIsMerchantSectionValid}
					/>
				)}
				{currentTab === 'businessInformation' && (
					<BusinessSection
						merchantData={merchantData}
						isLoading={isLoading}
						setIsSectionValid={setIsBusinessSectionValid}
					/>
				)}
				{currentTab === 'bankInformation' && (
					<BankSection
						merchantData={merchantData}
						isLoading={isLoading}
						setIsSectionValid={setIsBankSectionValid}
					/>
				)}
			</PageWithNavigation>
		</div>
	);
};
